import { RunContext } from "components/Run/Context";
import { RunElementsProvider } from "components/Run/RunElementsContext";
import PageLoading from "components/loading/PageLoading";
import Callout from "ds/components/Callout";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import { ObservabilityProcessingStatus } from "types/generated";

import NavigationBar from "../../elements/NavigationBar";
import ExportTracesButton from "../../elements/ExportTraces/Button";
import RunPageWrapper from "../../elements/RunPageWrapper";
import RunObservabilityEmptyState from "./EmptyState";
import PhaseExecutionTime from "./PhaseExecutionTime";
import RunObservabilityStatTiles from "./StatTiles";
import { useRunObservabilityData } from "./useRunObservabilityData";

const RunObservability = () => {
  const { run } = useTypedContext(RunContext);

  const metrics = run.observabilityStatus?.metrics;
  const isAvailable = metrics === ObservabilityProcessingStatus.Available;

  const { bars, tiles, comparison, hasContent, loading, error } = useRunObservabilityData();

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <PageInfoHeader title="Observability">
        <ExportTracesButton />
      </PageInfoHeader>

      <RunElementsProvider>
        {metrics !== undefined && metrics !== ObservabilityProcessingStatus.Available && (
          <RunObservabilityEmptyState status={metrics} />
        )}

        {isAvailable && (
          <>
            {loading && !hasContent && <PageLoading />}

            {!loading && !hasContent && (
              <RunObservabilityEmptyState status={ObservabilityProcessingStatus.Unavailable} />
            )}

            {hasContent && (
              <>
                {comparison && comparison.runCount < comparison.requiredRunCount && (
                  <Callout variant="warning" title="No comparison with previous runs">
                    Phase timings for this run are shown. To judge them, this page needs{" "}
                    {comparison.requiredRunCount} finished tracked runs that applied in the{" "}
                    {comparison.windowDays} days before this run, and found {comparison.runCount}.
                    Without them it can't say whether the run was slower than usual.
                  </Callout>
                )}

                <RunPageWrapper>
                  <RunObservabilityStatTiles tiles={tiles} />
                  <PhaseExecutionTime bars={bars} />
                </RunPageWrapper>
              </>
            )}
          </>
        )}

        <NavigationBar next={run.next?.id} previous={run.previous?.id} route="observability" />
      </RunElementsProvider>
    </>
  );
};

export default RunObservability;
